import { inject, Service } from '@angular/core'

import { ROLES } from '@core/data/models'
import { AppStore } from '@core/store'
import { VenuesService } from '@features/venues'
import { InvoiceOutcome, InvoiceResult, InvoiceStatus } from '../../models/tickets.model'
import { TicketsApi } from '../tickets-api/tickets-api'

/** Tope de peso del archivo; la lambda rechaza los muy grandes. */
const MAX_SIZE_MB = 5

/** La lambda lee la imagen: solo acepta formatos que pueda mirar. */
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp']

const NO_SESSION = 'Tu sesión terminó. Vuelve a iniciar sesión para subir tu ticket.'
const NO_VENUE = 'No encontramos el centro de consumo de tu cuenta. Escríbele a tu ejecutivo para que lo revise.'
const NOT_A_LEADER = 'Solo el líder del centro de consumo puede subir tickets.'
const GENERIC_ERROR = 'No pudimos subir tu ticket. Inténtalo de nuevo.'
const UNREADABLE = 'No pudimos abrir esta imagen. Prueba con otra foto.'

/**
 * Qué se le dice al participante en cada final y si tiene sentido volver a
 * intentar con el mismo archivo. El título solo se usa cuando la carga sale bien.
 */
const OUTCOMES: Record<InvoiceStatus, { ok: boolean; retryable: boolean; title: string; message: string }> = {
  registered: { ok: true, retryable: false, title: '¡Ticket registrado!', message: 'Las botellas de este ticket ya suman para tu centro. En unos minutos las verás en tu avance.' },
  duplicate: { ok: true, retryable: false, title: 'Este ticket ya estaba registrado', message: 'Ya lo habías subido antes y sus botellas ya cuentan. No tienes que hacer nada más.' },
  review: { ok: true, retryable: false, title: 'Tu ticket está en revisión', message: 'Lo vamos a revisar para confirmar tus botellas. Te avisaremos cuando esté listo; no hace falta que lo subas otra vez.' },
  rejected: { ok: false, retryable: false, title: '', message: 'No pudimos leer este ticket. Toma otra foto con buena luz, en la que se vea completo y se lean las claves y cantidades.' },
  conflict: { ok: false, retryable: true, title: '', message: 'Algo falló mientras subíamos tu ticket. Inténtalo de nuevo.' },
  retry: { ok: false, retryable: true, title: '', message: 'No pudimos conectarnos. Revisa tu conexión e inténtalo de nuevo en un momento.' }
}

/**
 * Carga de tickets sobre `TicketsApi`. Nunca lanza: devuelve el mensaje que el
 * modal muestra, porque los errores de esta pantalla son de ese formulario.
 *
 * El ticket se le carga solo al centro de consumo del gerente, que sale de sus
 * tags `cdc`: el reto es del centro, no del gerente.
 */
@Service()
export class TicketsService {
  readonly #api = inject(TicketsApi)
  readonly #store = inject(AppStore)
  readonly #venues = inject(VenuesService)

  /**
   * El id del intento sobrevive a los fallos: reintentar el mismo archivo repite
   * la misma solicitud y la lambda no la cuenta dos veces.
   */
  #attempt?: { key: string; requestId: string }

  async uploadInvoice(file: File): Promise<InvoiceResult> {
    const user = this.#store.currentUser()
    if (!user) return this.#fail(NO_SESSION)

    // El ejecutivo atiende varios centros: no hay uno solo al que cargarle el ticket.
    if (user.role === ROLES.EXECUTIVE) return this.#fail(NOT_A_LEADER)

    const invalid = this.#validate(file)
    if (invalid) return this.#fail(invalid)

    const venue = await this.#venues.getMyVenue()
    if (!venue) return this.#fail(NO_VENUE)

    const requestId = this.#requestIdFor(file)

    try {
      const base64 = await this.#toBase64(file)
      const outcome = await this.#api.uploadInvoice({ requestId, uid: venue.id, contentType: file.type, base64 })

      return this.#resultOf(outcome)
    } catch (error) {
      // Aquí solo llega lo que no vino de la lambda: leer el archivo, sobre todo.
      return this.#fail(error instanceof Error ? error.message : GENERIC_ERROR)
    }
  }

  #resultOf(outcome: InvoiceOutcome): InvoiceResult {
    const { ok, retryable, title, message } = OUTCOMES[outcome.status]

    // El intento solo se guarda mientras un reintento deba repetir el mismo id.
    if (!retryable || outcome.status === 'conflict') this.#attempt = undefined

    // Los códigos quedan en consola: son para nosotros, no para el participante.
    // Van también cuando la carga «salió bien» pero no quedó registrada, que es
    // justo lo que hay que poder rastrear con el `submission_id`.
    if (outcome.status !== 'registered' && outcome.reasonCodes.length) {
      console.error('Ticket sin registrar:', outcome.status, outcome.reasonCodes, outcome.nextAction, outcome.submissionId)
    }

    // El mensaje de la lambda es para nosotros: va a consola y el participante ve el suyo.
    if (outcome.message) console.warn('Mensaje de la lambda:', outcome.message)

    return { ok, retryable, title, message }
  }

  #fail(message: string): InvoiceResult {
    return { ok: false, title: '', message, retryable: false }
  }

  #validate(file: File): string {
    if (!ALLOWED_TYPES.includes(file.type)) return 'Sube una foto en formato JPG, PNG o WEBP.'
    if (file.size > MAX_SIZE_MB * 1024 * 1024) return `La foto pesa más de ${MAX_SIZE_MB} MB. Prueba con una más liviana.`

    return ''
  }

  /** Mismo archivo, mismo id: es un reintento y no una carga nueva. */
  #requestIdFor(file: File): string {
    const key = `${file.name}:${file.size}:${file.lastModified}`
    if (this.#attempt?.key === key) return this.#attempt.requestId

    const requestId = crypto.randomUUID()
    this.#attempt = { key, requestId }

    return requestId
  }

  /** La lambda espera solo el base64, sin el `data:` del lector. */
  async #toBase64(file: File): Promise<string> {
    const reader = new FileReader()

    const content = await new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(String(reader.result))
      reader.onerror = () => reject(new Error(UNREADABLE))
      reader.readAsDataURL(file)
    })

    const base64 = content.split(',')[1]
    if (!base64) throw new Error(UNREADABLE)

    return base64
  }
}
